import { getWorkDate } from "@components/inputs/date/utils";
import { IGetValueArgs, TGetValueFn, TInfoValue, TValidatorFn } from "@components/smart/FieldInfo";
import { SingleBusinessPartnerDef } from "@components/smart/GeneralFieldDefinition";
import {
    CompanyEntity,
    CompanyLegalAddressEntity,
    CompanyVatStatusTemporalCurrentEntity,
    CompanyVatStatusTemporalEntity,
    EntitySetName,
    GovernmentInstitutionEntity,
    ICompanyEntity,
    ICompanySettingEntity,
    ICompanyVatStatusTemporalEntity,
    IVatStatusEntity
} from "@odata/GeneratedEntityTypes";
import {
    AccountingCode,
    CompanyStateCode,
    GovernmentInstitutionTypeCode,
    VatStatementFrequencyCode,
    VatStatusCode
} from "@odata/GeneratedEnums";
import { OData } from "@odata/OData";
import { transformToODataString } from "@odata/OData.utils";
import { TFieldDefinition } from "@pages/PageUtils";
import { TestContext } from "yup";

import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, ValueType } from "../../enums";
import { TValue } from "../../global.types";
import BindingContext, { createPath } from "../../odata/BindingContext";
import { getUtcDayjs } from "../../types/Date";
import { FormStorage } from "../../views/formView/FormStorage";

export const ossBankAccountPath = "##OssBankAccount##";
export const companyTypeSettingsPath = BindingContext.localContext("CompanyTypeSettings");

export const BusinessAccountingTypes = [AccountingCode.AccountingForBusiness, AccountingCode.CashBasisAccounting];
export const NonProfitAccountingTypes = [AccountingCode.AccountingForNonprofitOrganizations];

export async function handleCompanyCreated(context: IAppContext, newId: number): Promise<void> {
    const previousCompanyId = context.getCompany()?.Id;
    await context.updateCompanies();
    await context.setCurrentCompanyId(newId);

    if (previousCompanyId === newId) {
        context.rerenderApp();
    }
}

export function isActiveCompany(company: ICompanyEntity): boolean {
    return [CompanyStateCode.Initialized].includes(company.StateCode);
}

export function getVatDeductionCoefficientCurrentYear(): number {
    return (getWorkDate()).getFullYear();
}

export async function getCompanySettings(oData: OData): Promise<ICompanySettingEntity> {
    let data: ICompanySettingEntity;
    try {
        const res = await oData.getEntitySetWrapper(EntitySetName.CompanySettings)
            .query().top(1)
            .fetchData<ICompanySettingEntity[]>();
        data = res.value?.[0] ?? {};
        // set default values
        data.InitialYear = data.InitialYear ?? getUtcDayjs().get("year");
    } catch (e) {
        return null;
    }

    return data;
}

export const VatReducedDeductionCoefficient = BindingContext.localContext("VatReducedDeductionCoefficient");

const getVatStatusIndex = (args: IGetValueArgs): number => {
    const bc = args.bindingContext.getParent();
    const company = args.storage.getEntity<ICompanyEntity>();

    // access TemporalPropertyBag directly instead of using getCompanyVatStatuses,
    // we want to work with the current state of temporal dialog
    const vatStatuses = company.TemporalPropertyBag;
    return vatStatuses.findIndex(vatStatus => {
        const vatStatusBc = bc.removeKey().addKey(vatStatus);

        return bc.isSame(vatStatusBc);
    });
};

export const isNotLastVatStatus: TInfoValue<boolean> = (args: IGetValueArgs) => {
    const itemIndex = getVatStatusIndex(args);

    // access TemporalPropertyBag directly instead of using getCompanyVatStatuses,
    // we want to work with the current state of temporal dialog
    const company = args.storage.getEntity<ICompanyEntity>();
    const vatStatuses = company.TemporalPropertyBag;

    return itemIndex !== vatStatuses.length - 1;
};

export const getIsNonVatRegisteredFieldDisabled = (ignorePreviousItem?: boolean): TInfoValue<boolean> => {
    return (args: IGetValueArgs) => {
        const vatStatusBc = args.bindingContext.getParent().navigate(CompanyVatStatusTemporalEntity.VatStatus);
        const vatStatus = args.storage.getValue(vatStatusBc) as IVatStatusEntity;
        const { previousItem } = getValidatorItems(args);

        return isNotLastVatStatus(args) || vatStatus?.Code !== VatStatusCode.VATRegistered
                || (!ignorePreviousItem && (vatStatus?.Code === VatStatusCode.VATRegistered && previousItem?.VatStatusCode === VatStatusCode.NotVATRegistered));
    };
};

export const isVatRegistered: TGetValueFn<boolean> = (args: IGetValueArgs): boolean => {
    const vatStatusCode: VatStatusCode = args.storage.getValueByPath(createPath(CompanyEntity.CurrentTemporalPropertyBag, CompanyVatStatusTemporalCurrentEntity.VatStatusCode));

    return vatStatusCode === VatStatusCode.VATRegistered;
};

const getValidatorItems = (args: IGetValueArgs) => {
    const item = args.storage.getValue(args.bindingContext.getParent()) as ICompanyVatStatusTemporalEntity;
    const items = args.storage.getValue(args.bindingContext.getParent().removeKey()) as ICompanyVatStatusTemporalEntity[];
    const itemIndex = items.findIndex((i: ICompanyVatStatusTemporalEntity) => i === item);
    const previousItem = items[itemIndex - 1];

    return {
        item, items, itemIndex, previousItem
    };
};

const validatorVatFrequencyDate = (args: IGetValueArgs, validationVatFrequencyCode: VatStatementFrequencyCode) => {
    const { item, previousItem } = getValidatorItems(args);

    const previousVatStatusCode = previousItem?.VatStatusCode as VatStatusCode;
    const vatFrequencyCode = item?.VatStatementFrequencyCode as VatStatementFrequencyCode;
    const previousVatFrequencyCode = previousItem?.VatStatementFrequencyCode as VatStatementFrequencyCode;

    if (
            [VatStatusCode.NotVATRegistered, VatStatusCode.IdentifiedPerson].includes(previousVatStatusCode)
            || [VatStatusCode.NotVATRegistered, VatStatusCode.IdentifiedPerson].includes(item.VatStatusCode as VatStatusCode)
            || vatFrequencyCode === null
            || vatFrequencyCode === previousVatFrequencyCode
            || vatFrequencyCode !== validationVatFrequencyCode
    ) {
        return true;
    }

    const dateValidFrom = getUtcDayjs(item.DateValidFrom);

    return dateValidFrom.date() === 1 && dateValidFrom.month() === 0;
};

export const validatorVatFrequencyQuarterlyDate: TValidatorFn = (value: TValue, args: IGetValueArgs, testContext?: TestContext) => {
    return validatorVatFrequencyDate(args, VatStatementFrequencyCode.Quarterly);
};

export const validatorVatFrequencyMonthlyDate: TValidatorFn = (value: TValue, args: IGetValueArgs, testContext?: TestContext) => {
    return validatorVatFrequencyDate(args, VatStatementFrequencyCode.Monthly);
};

export const partnerAresFields = [
    {
        from: "Name",
        to: CompanyEntity.LegalName
    },
    {
        from: "LegalNumber",
        to: CompanyEntity.LegalNumber
    },
    {
        from: "TaxNumber",
        to: CompanyEntity.TaxNumber
    },
    {
        from: "BillingAddress/Street",
        to: createPath(CompanyEntity.LegalAddress, CompanyLegalAddressEntity.Street)
    },
    {
        from: "BillingAddress/HouseNumber",
        to: createPath(CompanyEntity.LegalAddress, CompanyLegalAddressEntity.HouseNumber)
    },
    {
        from: "BillingAddress/StreetNumber",
        to: createPath(CompanyEntity.LegalAddress, CompanyLegalAddressEntity.StreetNumber)
    },
    {
        from: "BillingAddress/City",
        to: createPath(CompanyEntity.LegalAddress, CompanyLegalAddressEntity.City)
    },
    {
        from: "BillingAddress/Country",
        to: createPath(CompanyEntity.LegalAddress, CompanyLegalAddressEntity.Country)
    },
    {
        from: "BillingAddress/PostalCode",
        to: createPath(CompanyEntity.LegalAddress, CompanyLegalAddressEntity.PostalCode)
    },
    {
        from: "MainActivity",
        to: CompanyEntity.CzNace
    }
];

export const CompanySingleBusinessPartnerDef = {
    ...SingleBusinessPartnerDef,
    columns: [{
        id: CompanyEntity.LegalName
    }, {
        id: CompanyEntity.LegalNumber
    }]
};

export const hasPayrollEvidencePath = "##PayrollEvidence##";
export const hasGrantPath = "##GrantEvidence";

export const hasPayrollEnabled = (args: IGetValueArgs): boolean => {
    const accountingCode = args.storage.data.entity.AccountingCode as AccountingCode;
    return !!args.storage.getValueByPath(hasPayrollEvidencePath) && [AccountingCode.AccountingForBusiness, AccountingCode.AccountingForNonprofitOrganizations].includes(accountingCode);
};

export const canEditCsszIdentificationNumber = (args: IGetValueArgs): boolean => {
    const origEntity = (args.storage as FormStorage).getOrigEntity<ICompanyEntity>();
    return !origEntity?.CsszIdentificationNumber;
};

export const canEditOtherInstitutionsIdentificationNumber = (args: IGetValueArgs): boolean => {
    const origEntity = (args.storage as FormStorage).getOrigEntity<ICompanyEntity>();
    return !origEntity?.OtherInstitutionsIdentificationNumber;
};

export const canEditDefaultStatutoryInsuranceCompanyRate = (args: IGetValueArgs): boolean => {
    const origEntity = (args.storage as FormStorage).getOrigEntity<ICompanyEntity>();
    return !origEntity?.DefaultStatutoryInsuranceCompanyRate;
};

export const getCompanyGovernmentInstitutionDef = (type: GovernmentInstitutionTypeCode): TFieldDefinition => {
    return {
        ...getGovernmentInstitutionDef(type),
        isVisible: hasPayrollEnabled,
        isRequired: hasPayrollEnabled,
        clearIfInvisible: false,
    };
};

export const getGovernmentInstitutionDef = (type: GovernmentInstitutionTypeCode): TFieldDefinition => {
    return {
        type: FieldType.ComboBox,
        width: BasicInputSizes.L,
        fieldSettings: {
            displayName: "Name"
        },
        ...getGovernmentInstitutionFilterDefByType(type)
    };
};

export const getGovernmentInstitutionFilterDefByType = (type: GovernmentInstitutionTypeCode): TFieldDefinition => {
    return {
        filter: {
            select: () => {
                return `${GovernmentInstitutionEntity.GovernmentInstitutionTypeCode} eq ${transformToODataString(type, ValueType.String)}`;
            }
        }
    };
};